import type { BlogDraftPreviewCardMetadata } from '@wix/ai-scheduling-assistant-common-lib/entities';
import { EMAIL_REVIEW_LEAD, emailReviewMessage, parseSentEmailReview, readOnlyViewDocument, type ReviewBridgeOptions, type SentReviewRound } from './email-review';
import type { ReviewComment, SentReviewComment } from './site-review';

export interface BlogDraftPreviewCardData extends Required<Pick<BlogDraftPreviewCardMetadata, 'metaSiteId' | 'draftPostId'>> {
  title: string;
}

export interface BlogDraftPreview {
  title: string;
  html: string;
  heroImageUrl: string;
  sitePreviewUrl?: string;
}

const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const MAX_TITLE = 1000;
const MAX_ID = 128;

function text(value: unknown, cap: number): string {
  return typeof value === 'string' ? value.replace(/\s+/g, ' ').trim().slice(0, cap) : '';
}

export function readBlogDraftPreviewCard(metadata: unknown): BlogDraftPreviewCardData | null {
  const raw = (metadata as { blogDraftPreviewCard?: unknown } | null | undefined)?.blogDraftPreviewCard;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const card = raw as Record<string, unknown>;
  const metaSiteId = text(card.metaSiteId, MAX_ID);
  const draftPostId = text(card.draftPostId, MAX_ID);
  if (!GUID.test(metaSiteId) || !GUID.test(draftPostId)) return null;
  return { metaSiteId, draftPostId, title: text(card.title, MAX_TITLE) };
}

function escapeHtml(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function httpsUrl(value: string): string | null {
  try {
    const url = new URL(value.trim());
    return url.protocol === 'https:' ? url.toString() : null;
  } catch {
    return null;
  }
}

const ARTICLE_STYLE = [
  ':root{color-scheme:light}',
  'html,body{background:white;color:black}',
  'body{margin:0;padding:24px 16px 48px}',
  'article{max-width:760px;margin:0 auto;font:16px/1.62 system-ui,-apple-system,"Segoe UI",Roboto,sans-serif;overflow-wrap:anywhere}',
  'h1{font-size:28px;line-height:1.25;margin:0 0 20px}',
  'img,video,iframe,figure{max-width:100%}',
  'img,video{height:auto}',
  '.hero{display:block;width:100%;margin:0 0 24px;border-radius:12px}',
  'a{color:LinkText}',
  'blockquote,caption,figcaption{color:dimgray}',
].join('');

const ARTICLE_CSP = "script-src 'none'; form-action 'none'; base-uri 'none'; img-src https: data:";

export type BlogSiteLayout = 'phone' | 'desktop';

export function blogSitePreviewAddress(raw: string, layout: BlogSiteLayout, ownOrigin?: string): string | null {
  let url: URL;
  try {
    url = new URL(raw.trim());
  } catch {
    return null;
  }
  if (url.protocol !== 'https:' || !url.hostname || url.username || url.password) return null;
  if (ownOrigin && url.origin === ownOrigin) return null;
  url.searchParams.delete('showMobileView');
  if (layout === 'phone') url.searchParams.append('showMobileView', 'true');
  return url.toString();
}

export function blogDraftTitle(preview: BlogDraftPreview, cardTitle: string): string {
  return text(preview.title, MAX_TITLE) || text(cardTitle, MAX_TITLE);
}

export function blogDraftArticle(preview: BlogDraftPreview, title: string): string {
  const heading = escapeHtml(text(title, MAX_TITLE));
  const hero = httpsUrl(preview.heroImageUrl);
  return [
    '<!doctype html><html><head>',
    `<meta http-equiv="Content-Security-Policy" content="${ARTICLE_CSP}">`,
    `<title>${heading}</title>`,
    `<style>${ARTICLE_STYLE}</style>`,
    '</head><body><article>',
    heading ? `<h1>${heading}</h1>` : '',
    hero ? `<img class="hero" src="${escapeHtml(hero)}" alt="">` : '',
    preview.html,
    '</article></body></html>',
  ].join('');
}

const DOCUMENT_SHAPED = /<(?:html|head|body)[\s>]/i;

export function isSitePage(preview: BlogDraftPreview): boolean {
  return DOCUMENT_SHAPED.test(preview.html.replace(/<!--[\s\S]*?-->/g, ''));
}

export function blogDraftReviewHtml(preview: BlogDraftPreview, title: string): string {
  return isSitePage(preview) ? preview.html : blogDraftArticle(preview, title);
}

export const BLOG_POST_AREAS = ['post-title', 'post-description', 'post-hero-image'].map((hook) => `[data-hook="post"] [data-hook="${hook}"]`);

export function blogDraftReviewOptions(preview: BlogDraftPreview): ReviewBridgeOptions {
  return isSitePage(preview) ? { areas: BLOG_POST_AREAS, fit: true } : {};
}

export function blogDraftShowsPost(preview: BlogDraftPreview, parser: DOMParser = new DOMParser()): boolean {
  return !isSitePage(preview) || parser.parseFromString(preview.html, 'text/html').querySelector(BLOG_POST_AREAS.join(',')) !== null;
}

export function blogDraftViewerHtml(preview: BlogDraftPreview, title: string): string {
  return withArticlePolicy(blogDraftReviewHtml(preview, title));
}

export function blogDraftReadOnlyHtml(preview: BlogDraftPreview, title: string): string {
  return withArticlePolicy(readOnlyViewDocument(blogDraftReviewHtml(preview, title)));
}

/** Prefix the restrictive policy before any author markup; the HTML parser hoists it into head. */
function withArticlePolicy(html: string): string {
  return html.replace(/^(\s*<!doctype[^>]*>)?/i, `$1<meta http-equiv="Content-Security-Policy" content="${ARTICLE_CSP}">`);
}

export function blogDraftFileName(title: string): string {
  const name = text(title, 120).replace(/[\\/:*?"<>|]/g, '').trim();
  return `${name || 'Blog post'}.html`;
}

export const BLOG_REVIEW_LEAD = 'Address all comments on the blog post';

const SITE_LINE = /^Site: \S+$/;
const DRAFT_LINE = /^Draft: (\S+)$/;
const TITLE_LINE = /^Title: .*$/;

export function blogReviewMessage(draft: BlogDraftPreviewCardData, comments: readonly ReviewComment[]): string {
  const title = text(draft.title, MAX_TITLE);
  const head = [BLOG_REVIEW_LEAD, `Site: ${draft.metaSiteId}`, `Draft: ${draft.draftPostId}`, ...(title ? [`Title: ${title}`] : [])];
  const [, ...blocks] = emailReviewMessage({ metaSiteId: draft.metaSiteId, campaignId: draft.draftPostId, subject: '' }, comments).split('\n\n');
  return [head.join('\n'), ...blocks].join('\n\n');
}

export function parseSentBlogReview(message: string): SentReviewComment[] | null {
  const lines = message.replace(/\r\n?/g, '\n').trim().split('\n');
  if (lines[0]?.trim() !== BLOG_REVIEW_LEAD || !SITE_LINE.test(lines[1] ?? '') || !DRAFT_LINE.test(lines[2] ?? '')) return null;
  const start = TITLE_LINE.test(lines[3] ?? '') ? 4 : 3;
  return parseSentEmailReview([EMAIL_REVIEW_LEAD, 'Site: -', 'Campaign: -', ...lines.slice(start)].join('\n'));
}

export function readSentBlogReview(message: string): SentReviewRound | null {
  const comments = parseSentBlogReview(message);
  const id = comments ? DRAFT_LINE.exec(message.replace(/\r\n?/g, '\n').trim().split('\n')[2] ?? '')?.[1] : undefined;
  return comments && id ? { id: id.toLowerCase(), comments } : null;
}

export function blogReviewPageUrl(draftPostId: string): string {
  return `https://blog-review.invalid/${encodeURIComponent(draftPostId)}`;
}

export function blogReviewKey(draftPostId: string, conversationId: string | null): string {
  return `blog:${draftPostId}|${conversationId ?? ''}`;
}

interface BlogDraftCardRow {
  id?: string;
  finalId?: string;
  blogDraftPreviewCard?: BlogDraftPreviewCardData;
}

const rowKey = (row: BlogDraftCardRow): string | undefined => row.finalId ?? row.id;

export function latestBlogDraftCards<T extends BlogDraftCardRow>(rows: readonly T[]): Map<string, T> {
  const latest = new Map<string, T>();
  for (const row of rows) {
    if (rowKey(row) && row.blogDraftPreviewCard) latest.set(row.blogDraftPreviewCard.draftPostId, row);
  }
  return latest;
}

export function isLatestBlogDraftCard(latest: ReadonlyMap<string, BlogDraftCardRow>, row: BlogDraftCardRow): boolean {
  const key = rowKey(row);
  const last = row.blogDraftPreviewCard ? latest.get(row.blogDraftPreviewCard.draftPostId) : undefined;
  return key !== undefined && last !== undefined && rowKey(last) === key;
}

export type BlogReviewSendPlan<T> = { status: 'send' } | { status: 'busy' } | { status: 'newer'; row: T; card: BlogDraftPreviewCardData };

export function planBlogReviewSend<T extends BlogDraftCardRow>(opened: T, latest: ReadonlyMap<string, T>, streaming: boolean): BlogReviewSendPlan<T> {
  const row = opened.blogDraftPreviewCard ? latest.get(opened.blogDraftPreviewCard.draftPostId) : undefined;
  if (row?.blogDraftPreviewCard && !isLatestBlogDraftCard(latest, opened)) return { status: 'newer', row, card: row.blogDraftPreviewCard };
  return streaming ? { status: 'busy' } : { status: 'send' };
}
